// Das "/"-Menü für ein einfaches Textfeld (SMS-Karte; Runde 8, Agent 8D). Der Editor der E-Mail und der Notiz hat sein eigenes Menü
// (`kommunikation/Editor.tsx`); dieses hier macht dasselbe für eine `<textarea>`: ein Schrägstrich am Wortanfang öffnet die Liste der
// Textbausteine, weitere Buchstaben filtern nach Kürzel und Name, Pfeile wählen, Enter oder Tab fügt den Text ein, Escape schließt.
// Verwendung:
//   const slash = useSlashMenue(useTextbausteine("sms"), text, setText, feldRef);
//   <div className="bt-slash-rahmen"><textarea … {...slash.feldProps(text => setText(text))} />{slash.menue}</div>
import { useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import type { Baustein } from "../kommunikation/Editor";
import { htmlZuText } from "./htmlText";
import { T8D } from "./texte";
import "./slash.css";

const MUSTER = /(?:^|\s)\/([^\s/]{0,30})$/;
const HOECHSTENS = 8;

interface Stand {
  query: string;
  /** Stelle des Schrägstrichs im Text */
  start: number;
}

export interface SlashMenueErgebnis {
  /** Eigenschaften für die `<textarea>`: Änderung und Tasten */
  feldProps: (setText: (t: string) => void) => {
    onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
    onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
    onBlur: () => void;
  };
  menue: ReactNode;
  offen: boolean;
}

export function useSlashMenue(
  bausteine: Baustein[],
  text: string,
  setText: (t: string) => void,
  feld: RefObject<HTMLTextAreaElement | null>,
): SlashMenueErgebnis {
  const [stand, setStand] = useState<Stand | null>(null);
  const [aktiv, setAktiv] = useState(0);
  const zugeklappt = useRef<string | null>(null); // Escape: für diesen Text kein Menü mehr

  const treffer = useMemo(() => {
    if (!stand) return [];
    const q = stand.query.toLowerCase();
    return bausteine.filter((b) => !q || b.name.toLowerCase().includes(q)).slice(0, HOECHSTENS);
  }, [stand, bausteine]);

  const pruefen = (wert: string, cursor: number) => {
    const davor = wert.slice(0, cursor);
    const m = MUSTER.exec(davor);
    if (!m || zugeklappt.current === davor) {
      setStand(null);
      return;
    }
    setStand({ query: m[1], start: cursor - m[1].length - 1 });
    setAktiv(0);
  };

  const waehlen = (b: Baustein) => {
    if (!stand) return;
    const ende = stand.start + 1 + stand.query.length;
    const einsatz = htmlZuText(b.html); // liest `html` und zählt damit die Nutzung
    const neu = `${text.slice(0, stand.start)}${einsatz}${text.slice(ende)}`;
    const cursor = stand.start + einsatz.length;
    setText(neu);
    setStand(null);
    window.requestAnimationFrame(() => {
      const el = feld.current;
      if (el) {
        el.focus();
        el.setSelectionRange(cursor, cursor);
      }
    });
  };

  const feldProps: SlashMenueErgebnis["feldProps"] = (setzeText) => ({
    onChange: (e) => {
      setzeText(e.target.value);
      pruefen(e.target.value, e.target.selectionStart ?? e.target.value.length);
    },
    onKeyDown: (e) => {
      if (!stand) return;
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        zugeklappt.current = text.slice(0, e.currentTarget.selectionStart ?? text.length);
        setStand(null);
        return;
      }
      if (!treffer.length) return;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setAktiv((a) => (a + (e.key === "ArrowDown" ? 1 : treffer.length - 1)) % treffer.length);
      } else if (e.key === "Enter" || e.key === "Tab") {
        if (e.metaKey || e.ctrlKey) return; // Strg/⌘ + Enter sendet
        e.preventDefault();
        e.stopPropagation();
        waehlen(treffer[Math.min(aktiv, treffer.length - 1)]);
      }
    },
    onBlur: () => window.setTimeout(() => setStand(null), 120),
  });

  const menue = stand ? (
    <ul className="bt-slash" role="listbox" aria-label={T8D.slash.titel}>
      {treffer.length ? (
        treffer.map((b, i) => (
          <li
            key={b.id}
            role="option"
            aria-selected={i === aktiv}
            className={`bt-slash__eintrag${i === aktiv ? " bt-slash__eintrag--an" : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              waehlen(b);
            }}
            onMouseEnter={() => setAktiv(i)}
          >
            {b.name}
          </li>
        ))
      ) : (
        <li className="bt-slash__leer">{bausteine.length ? T8D.slash.keinTreffer : T8D.slash.keine}</li>
      )}
    </ul>
  ) : null;

  return { feldProps, menue, offen: stand !== null };
}
